import { Link } from "react-router-dom";
import { Building2, CreditCard, User } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { paymentMethodLabel } from "../../../lib/paymentLabel";
import { formatCents } from "../../../lib/sales";
import type { PaymentRow } from "../../../lib/payments";
import { fmtDate } from "../../ui";
import { PaymentRowMenu } from "./PaymentRowMenu";

/**
 * Date, invoice, client, method, reference, amount - the six things a ledger
 * row is, plus the 44px actions track.
 *
 * It lives here rather than in the list so the head and the rows import the
 * same constant: two separate track lists drift apart the first time somebody
 * adds a column, and grid says nothing when they do - the figures just move one
 * heading to the left.
 */
export const PAYMENT_COLS = "1.05fr .95fr minmax(0,1.5fr) .85fr minmax(0,1.3fr) 1fr 44px";

/** One payment in the ledger. */
export function PaymentListRow({
  payment,
  invoiceLabel,
  canManage,
  onDelete,
}: {
  payment: PaymentRow;
  /** Stands in for a missing invoice number, so the link is never just a dash. */
  invoiceLabel: string;
  canManage: boolean;
  onDelete: (row: PaymentRow) => void;
}) {
  const t = useT();
  const Kind = payment.client?.kind === "person" ? User : Building2;
  // The chip prints the same wording the filter above the table offers. Before
  // this the row printed the stored token, so a built-in read `pangaülekanne`
  // and "Mark as paid" read `manual`, in English as well as in Estonian.
  const method = paymentMethodLabel(payment.method, t, "");
  const number = payment.invoice?.number?.trim();

  return (
    // A div, not a link: the row ends in a menu button, and interactive content
    // inside an <a> is invalid HTML and swallows the button's clicks. The
    // invoice link stretches an ::after over the whole row instead, so the row
    // stays clickable end to end and the button still sits on top of it.
    <div className="crm-list-row relative" style={{ gridTemplateColumns: PAYMENT_COLS }}>
      {/* A payment has no name to initialise, so the disc says what kind of
          record this is rather than inventing two capitals out of a number. */}
      <span className="flex min-w-0 items-center gap-3">
        <span className="crm-av crm-av-soft" style={{ width: 34, height: 34 }} aria-hidden>
          <CreditCard className="size-4" />
        </span>
        <span className="crm-tnum crm-trunc text-[13px]">{fmtDate(payment.paidAt)}</span>
      </span>

      <span className="min-w-0">
        <Link
          to={`/sales/invoices/${payment.invoiceId}`}
          className="crm-prow-n crm-tnum crm-trunc block after:absolute after:inset-0 after:rounded-full"
        >
          {/* A number-less invoice still has a page to open, so the link falls
              back to the word rather than to a dash nobody can read aloud. */}
          {number || invoiceLabel}
        </Link>
      </span>

      <span className="crm-mut flex min-w-0 items-center gap-1.5 text-[12.5px]">
        {payment.client ? <Kind className="size-3.5 flex-none" /> : null}
        <span className="crm-trunc">{payment.client?.name ?? "-"}</span>
      </span>

      <span className="min-w-0">
        {method ? (
          // The label is nested rather than truncated on the chip itself: a chip
          // is inline-flex, and `text-overflow` on a flex container never
          // reaches the text inside it.
          <span className="crm-chip max-w-full">
            <span className="crm-trunc">{method}</span>
          </span>
        ) : (
          <span className="crm-mut text-[12.5px]">-</span>
        )}
      </span>

      <span className="crm-mut crm-trunc text-[12.5px]">{payment.reference ?? "-"}</span>

      {/* Money received, not money billed - the invoice total lives on the
          invoice. Formatted in the row's own currency, because a payment can be
          settled in something other than the ledger's default. */}
      <span className="crm-tnum justify-self-end text-[13.25px] font-bold">
        {formatCents(payment.amountCents, payment.currency)}
      </span>

      <PaymentRowMenu payment={payment} canManage={canManage} onDelete={onDelete} />
    </div>
  );
}
